<template>
  <section class="dp-layout">
    <!-- 左侧部门/班级树 -->
    <section class="dp-left">
      <DepartmentPane directory-scope="teaching" @tree-node-click="treeNodeClick" />
    </section>

    <!-- 右侧排班记录主区域 -->
    <section class="dp-right">
      <div v-if="Number(departmentId) > 0" class="all_page_border">
        <!-- 页面顶部标题 -->
        <div class="record-head">
          <div class="record-head__title-group">
            <span class="record-head__tag">校车管理 · 排班审计</span>
            <h2 class="record-head__title">{{ departmentName }} · 班级乘车排班记录</h2>
          </div>
          <div class="record-head__tip">
            <i class="el-icon-info"></i> 本页面为后台只读监控视图，班级每日乘车名单由各班班主任在移动端 H5 录入与调整
          </div>
        </div>

        <!-- 检索与筛选栏 -->
        <div class="filter-bar">
          <div class="filter-left">
            <span class="filter-label">排班日期：</span>
            <el-date-picker
              v-model="dateRange"
              type="daterange"
              size="small"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              value-format="yyyy-MM-dd"
              :picker-options="pickerOptions"
              @change="fetchData"
              style="width: 250px;"
            />

            <span class="filter-label" style="margin-left: 16px;">执行状态：</span>
            <el-select v-model="filterStatus" size="small" placeholder="全部状态" @change="fetchData" style="width: 130px;">
              <el-option label="全部状态" :value="-1" />
              <el-option label="待执行" :value="0" />
              <el-option label="部分完成" :value="1" />
              <el-option label="全部完成" :value="2" />
              <el-option label="已取消" :value="3" />
            </el-select>

            <el-button size="small" type="primary" icon="el-icon-search" @click="fetchData" style="margin-left: 12px;">
              查询
            </el-button>
            <el-button size="small" icon="el-icon-refresh" @click="resetQuery">
              重置
            </el-button>
            <el-button size="small" type="success" icon="el-icon-download" :loading="exporting" @click="handleExport">
              导出 Excel
            </el-button>
          </div>
        </div>

        <!-- 排班主记录表格（放入 tbl-scroll-host 弹性铺满右侧） -->
        <div class="tbl-scroll-host">
          <el-table
            :data="recordList"
            v-loading="loading"
            size="small"
            border
            stripe
            height="100%"
            style="width: 100%;"
            class="custom-tbl"
            :header-cell-style="{ background: '#f5f7fa', color: '#606266', fontWeight: 'bold' }"
          >
            <el-table-column label="排班日期" width="120" align="center">
              <template slot-scope="{ row }">
                <span style="font-weight: 600;">{{ row.ScheduleDate || row.scheduleDate }}</span>
              </template>
            </el-table-column>
            <el-table-column label="星期" width="70" align="center">
              <template slot-scope="{ row }">
                <el-tag size="mini" type="info">{{ row.WeekDayName || row.weekDayName }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="年级" prop="GradeName" width="100" align="center">
              <template slot-scope="{ row }">
                {{ row.GradeName !== undefined ? row.GradeName : row.gradeName }}
              </template>
            </el-table-column>
            <el-table-column label="班级" prop="ClassName" min-width="120">
              <template slot-scope="{ row }">
                <span style="font-weight: 600; color: #303133;">{{ row.ClassName !== undefined ? row.ClassName : row.className }}</span>
              </template>
            </el-table-column>
            <el-table-column label="统一坐车时间" min-width="160" align="center">
              <template slot-scope="{ row }">
                <div class="ride-time-tags">
                  <el-tag
                    v-for="(t, idx) in getRowRideTimes(row)"
                    :key="idx"
                    size="mini"
                    type="warning"
                    class="ride-time-tag"
                  >
                    <i class="el-icon-time"></i> {{ t }}
                  </el-tag>
                  <span v-if="!getRowRideTimes(row).length" class="ride-time-empty">-</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="计划乘车" width="100" align="center">
              <template slot-scope="{ row }">
                <span class="count-badge count-plan">
                  {{ (row.TotalCount !== undefined ? row.TotalCount : row.totalCount) }} 人
                </span>
              </template>
            </el-table-column>
            <el-table-column label="总次数" width="90" align="center">
              <template slot-scope="{ row }">
                <span class="count-badge count-slot">
                  {{ (row.SlotCount !== undefined ? row.SlotCount : (row.slotCount || 0)) }} 次
                </span>
              </template>
            </el-table-column>
            <el-table-column label="已推送离校" width="110" align="center">
              <template slot-scope="{ row }">
                <span class="count-badge count-success">
                  {{ (row.ProcessedCount !== undefined ? row.ProcessedCount : row.processedCount) }} 人
                </span>
              </template>
            </el-table-column>
            <el-table-column label="已推送次数" width="100" align="center">
              <template slot-scope="{ row }">
                <span class="count-badge count-slot-done">
                  {{ (row.ProcessedSlotCount !== undefined ? row.ProcessedSlotCount : (row.processedSlotCount || 0)) }} 次
                </span>
              </template>
            </el-table-column>
            <el-table-column label="执行状态" width="100" align="center">
              <template slot-scope="{ row }">
                <el-tag size="small" :type="getStatusTagType(row.Status !== undefined ? row.Status : row.status)">
                  {{ row.StatusName !== undefined ? row.StatusName : row.statusName }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作班主任" min-width="100" align="center">
              <template slot-scope="{ row }">
                <i class="el-icon-user" style="color: #909399; margin-right: 2px;"></i>
                {{ row.CreatedTeacherName || row.createdTeacherName || '班主任' }}
              </template>
            </el-table-column>
            <el-table-column label="最后设置时间" width="150" align="center">
              <template slot-scope="{ row }">
                <span style="color: #909399; font-size: 12px;">{{ row.ModifyTime || row.modifyTime || row.CreateTime || row.createTime }}</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="110" align="center" fixed="right">
              <template slot-scope="{ row }">
                <el-button
                  size="mini"
                  type="primary"
                  plain
                  icon="el-icon-tickets"
                  @click="openStudentDetails(row)"
                >
                  乘车名单
                </el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 底部分页固定栏 -->
        <el-col :span="24" class="toolbar">
          <el-pagination
            layout="total, sizes, prev, pager, next, jumper"
            :current-page="page"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="pageSize"
            :total="totalCount"
            @size-change="onPageSizeChange"
            @current-change="onPageChange"
            style="float: right"
          />
        </el-col>
      </div>

      <!-- 未选部门引导占位 -->
      <div v-else class="empty-placeholder">
        <i class="el-icon-office-building"></i>
        <p>请在左侧组织架构树中选择学校 / 年级 / 班级查看乘车排班记录</p>
      </div>
    </section>

    <!-- 乘车名单弹窗：固定高度 + 搜索 + 分页 -->
    <el-dialog
      :title="detailDialogTitle"
      :visible.sync="detailDialogVisible"
      width="780px"
      custom-class="schedule-students-dialog"
      append-to-body
      @closed="onDetailDialogClosed"
    >
      <div class="detail-meta-banner">
        <div class="meta-item"><span class="k">排班日期</span><span class="v">{{ currentSchedule.date }}（{{ currentSchedule.weekDay }}）</span></div>
        <div class="meta-item"><span class="k">班级</span><span class="v">{{ currentSchedule.className }}</span></div>
        <div class="meta-item"><span class="k">班主任</span><span class="v">{{ currentSchedule.teacherName }}</span></div>
        <div class="meta-item meta-item--times">
          <span class="k">统一时间</span>
          <span class="v ride-time-tags">
            <el-tag
              v-for="(t, idx) in parseRideTimes(currentSchedule.unifiedTime)"
              :key="'dlg-' + idx"
              size="mini"
              type="warning"
              class="ride-time-tag"
            >
              <i class="el-icon-time"></i> {{ t }}
            </el-tag>
            <span v-if="!parseRideTimes(currentSchedule.unifiedTime).length">-</span>
          </span>
        </div>
      </div>

      <div class="detail-toolbar">
        <el-input
          v-model="detailKeyword"
          size="small"
          clearable
          prefix-icon="el-icon-search"
          placeholder="搜索学生姓名 / 卡号"
          style="width: 240px;"
          @input="onDetailSearch"
          @clear="onDetailSearch"
        />
        <span class="detail-total-tip">共 <b>{{ detailFilteredTotal }}</b> 人</span>
      </div>

      <el-table
        :data="pagedStudentDetailList"
        v-loading="detailLoading"
        size="small"
        border
        stripe
        height="360"
        style="width: 100%;"
      >
        <el-table-column type="index" label="序号" width="55" align="center" :index="detailIndexMethod" />
        <el-table-column label="学生姓名" min-width="100">
          <template slot-scope="{ row }">
            <span class="student-name">{{ row.StudentName || row.studentName }}</span>
          </template>
        </el-table-column>
        <el-table-column label="卡号" min-width="120" align="center" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <span class="card-no">{{ row.CardNumber || row.cardNumber || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="坐车时间" min-width="160" align="center">
          <template slot-scope="{ row }">
            <div class="ride-time-tags">
              <el-tag
                v-for="(t, idx) in (row.rideTimes || [])"
                :key="'rt-' + idx"
                size="mini"
                type="warning"
                class="ride-time-tag"
              >
                <i class="el-icon-time"></i> {{ t }}
              </el-tag>
              <span v-if="!(row.rideTimes && row.rideTimes.length)" class="ride-time-empty">-</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="推送状态" min-width="120" align="center">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="getStudentStatusTagType(row.processStatus)">
              {{ row.processStatusName }}
            </el-tag>
            <div v-if="row.slotCount > 1" class="slot-count-tip">共 {{ row.slotCount }} 个时段</div>
          </template>
        </el-table-column>
        <el-table-column label="执行时间" min-width="150" align="center" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <span class="muted-text">{{ row.processedTime || '-' }}</span>
          </template>
        </el-table-column>
      </el-table>

      <div class="detail-pager">
        <el-pagination
          layout="total, prev, pager, next"
          :current-page="detailPage"
          :page-size="detailPageSize"
          :total="detailFilteredTotal"
          @current-change="onDetailPageChange"
        />
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button size="small" type="primary" @click="detailDialogVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </section>
</template>

<script>
import DepartmentPane from '@/components/DepartmentPane.vue';
import {
  getClassScheduleRecordList,
  getScheduleStudentDetails,
  exportClassScheduleRecordList
} from '@/api/modules/schoolBusSchedule';

export default {
  name: 'SchoolBusScheduleRecordList',
  components: {
    DepartmentPane
  },
  data() {
    const today = new Date().toISOString().slice(0, 10);
    return {
      departmentId: 0,
      departmentName: '',
      isClassNode: false,

      // 筛选条件
      dateRange: [today, today],
      filterStatus: -1,

      // 表格数据
      recordList: [],
      loading: false,
      exporting: false,
      page: 1,
      pageSize: 10,
      totalCount: 0,

      // 乘车名单弹窗
      detailDialogVisible: false,
      detailLoading: false,
      studentDetailAllList: [],
      detailKeyword: '',
      detailPage: 1,
      detailPageSize: 8,
      currentSchedule: {
        date: '',
        weekDay: '',
        className: '',
        unifiedTime: '',
        teacherName: ''
      },

      pickerOptions: {
        shortcuts: [
          {
            text: '今天',
            onClick(picker) {
              const d = new Date().toISOString().slice(0, 10);
              picker.$emit('pick', [d, d]);
            }
          },
          {
            text: '最近 7 天',
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 6);
              picker.$emit('pick', [start.toISOString().slice(0, 10), end.toISOString().slice(0, 10)]);
            }
          },
          {
            text: '未来 7 天',
            onClick(picker) {
              const start = new Date();
              const end = new Date();
              end.setTime(end.getTime() + 3600 * 1000 * 24 * 6);
              picker.$emit('pick', [start.toISOString().slice(0, 10), end.toISOString().slice(0, 10)]);
            }
          }
        ]
      }
    };
  },
  computed: {
    detailDialogTitle() {
      return `${this.currentSchedule.className || '班级'} · 乘车学生名单`;
    },
    /** 按姓名/卡号过滤后的名单 */
    filteredStudentDetailList() {
      const kw = (this.detailKeyword || '').trim().toLowerCase();
      const list = this.studentDetailAllList || [];
      if (!kw) return list;
      return list.filter((row) => {
        const name = String(row.StudentName || row.studentName || '').toLowerCase();
        const card = String(row.CardNumber || row.cardNumber || '').toLowerCase();
        return name.indexOf(kw) !== -1 || card.indexOf(kw) !== -1;
      });
    },
    detailFilteredTotal() {
      return this.filteredStudentDetailList.length;
    },
    /** 当前页名单 */
    pagedStudentDetailList() {
      const start = (this.detailPage - 1) * this.detailPageSize;
      return this.filteredStudentDetailList.slice(start, start + this.detailPageSize);
    }
  },
  methods: {
    /**
     * 将逗号分隔的统一坐车时间拆成标签数组。创建于 2026-09-16
     */
    parseRideTimes(raw) {
      if (!raw) return [];
      return String(raw)
        .split(/[,，;；\s]+/)
        .map((t) => t.trim())
        .filter(Boolean);
    },

    getRowRideTimes(row) {
      if (!row) return [];
      const raw = row.UnifiedRideTimes !== undefined
        ? row.UnifiedRideTimes
        : (row.unifiedRideTimes || row.UnifiedRideTime || row.unifiedRideTime);
      return this.parseRideTimes(raw);
    },

    treeNodeClick(data) {
      if (!data) return;
      const deptId = data.value !== undefined ? data.value : data.id;
      if (deptId === undefined || deptId === null || deptId === '' || Number(deptId) <= 0) return;

      // 部门 ID 保留字符串，避免大整型精度丢失
      this.departmentId = deptId;
      this.departmentName = data.label || data.name || '';
      const orgType = String(data.DeptOrgType || data.deptOrgType || '').toLowerCase();
      this.isClassNode = orgType === 'class' || !data.children || data.children.length === 0;

      this.page = 1;
      this.fetchData();
    },

    /**
     * 拉取排班记录列表。最后修改：2026-09-16
     */
    async fetchData() {
      if (!this.departmentId) return;

      this.loading = true;
      try {
        const startDate = this.dateRange && this.dateRange[0] ? this.dateRange[0] : '';
        const endDate = this.dateRange && this.dateRange[1] ? this.dateRange[1] : '';

        const params = {
          schoolDepartmentId: this.departmentId,
          classDepartmentId: this.isClassNode ? this.departmentId : 0,
          startDate,
          endDate,
          page: this.page,
          pageSize: this.pageSize
        };
        if (this.filterStatus >= 0) {
          params.status = this.filterStatus;
        }

        // API 层已解开 axios.data，这里直接是 MessageModel
        const res = await getClassScheduleRecordList(params);
        if (res && res.success && res.response) {
          const pageData = res.response;
          const rows = pageData.data || pageData.Data || [];
          this.recordList = Array.isArray(rows) ? rows.slice() : [];
          this.totalCount = pageData.dataCount != null
            ? pageData.dataCount
            : (pageData.DataCount != null ? pageData.DataCount : this.recordList.length);
        } else {
          this.recordList = [];
          this.totalCount = 0;
          if (res && res.msg) {
            this.$message.warning(res.msg);
          }
        }
      } catch (err) {
        this.recordList = [];
        this.totalCount = 0;
        this.$message.error('获取排班记录失败：' + (err.message || '网络异常'));
      } finally {
        this.loading = false;
      }
    },

    resetQuery() {
      const today = new Date().toISOString().slice(0, 10);
      this.dateRange = [today, today];
      this.filterStatus = -1;
      this.page = 1;
      this.fetchData();
    },

    /**
     * 导出当前筛选条件下的排班记录 Excel。创建于 2026-09-16
     */
    async handleExport() {
      if (!this.departmentId) {
        this.$message.warning('请先在左侧选择学校 / 年级 / 班级');
        return;
      }

      const startDate = this.dateRange && this.dateRange[0] ? this.dateRange[0] : '';
      const endDate = this.dateRange && this.dateRange[1] ? this.dateRange[1] : '';
      const params = {
        schoolDepartmentId: this.departmentId,
        classDepartmentId: this.isClassNode ? this.departmentId : 0,
        startDate,
        endDate
      };
      if (this.filterStatus >= 0) {
        params.status = this.filterStatus;
      }

      this.exporting = true;
      const loadingMsg = this.$message({
        message: '正在生成 Excel 导出文件，请稍候...',
        type: 'info',
        duration: 0,
        iconClass: 'el-icon-loading'
      });

      try {
        const res = await exportClassScheduleRecordList(params);
        loadingMsg.close();

        // 若后端返回 JSON 错误（blob 里是文本），给出提示
        const contentType = (res.headers && (res.headers['content-type'] || res.headers['Content-Type'])) || '';
        if (contentType.indexOf('application/json') !== -1) {
          const text = await new Response(res.data).text();
          let msg = '导出失败';
          try {
            const json = JSON.parse(text);
            msg = json.msg || json.message || msg;
          } catch (e) {
            /* ignore */
          }
          this.$message.error(msg);
          return;
        }

        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        const scopeName = this.departmentName || '排班记录';
        link.download = `班级排班记录_${scopeName}_${Date.now()}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        this.$message.success('导出成功');
      } catch (err) {
        loadingMsg.close();
        this.$message.error('导出 Excel 失败：' + (err.message || '请重试'));
      } finally {
        this.exporting = false;
      }
    },

    onPageChange(p) {
      this.page = p;
      this.fetchData();
    },

    onPageSizeChange(s) {
      this.pageSize = s;
      this.page = 1;
      this.fetchData();
    },

    async openStudentDetails(row) {
      const scheduleId = row.ScheduleId !== undefined ? row.ScheduleId : row.scheduleId;
      this.currentSchedule = {
        date: row.ScheduleDate || row.scheduleDate,
        weekDay: row.WeekDayName || row.weekDayName,
        className: row.ClassName !== undefined ? row.ClassName : row.className,
        unifiedTime: row.UnifiedRideTimes !== undefined ? row.UnifiedRideTimes : (row.unifiedRideTimes || row.UnifiedRideTime || row.unifiedRideTime),
        teacherName: row.CreatedTeacherName || row.createdTeacherName || '班主任'
      };

      this.detailKeyword = '';
      this.detailPage = 1;
      this.detailDialogVisible = true;
      this.detailLoading = true;
      this.studentDetailAllList = [];

      try {
        const res = await getScheduleStudentDetails({ scheduleId });
        if (res && res.success) {
          // 同一学生多时段合并为一行展示
          this.studentDetailAllList = this.mergeStudentDetailsByStudent(res.response || []);
        } else {
          this.studentDetailAllList = [];
          if (res && res.msg) {
            this.$message.warning(res.msg);
          }
        }
      } catch (err) {
        this.studentDetailAllList = [];
        this.$message.error('获取学生明细失败：' + (err.message || '网络异常'));
      } finally {
        this.detailLoading = false;
      }
    },

    /**
     * 将同一学生的多时段明细合并为一行。创建于 2026-09-16
     */
    mergeStudentDetailsByStudent(list) {
      const map = {};
      const order = [];
      (list || []).forEach((item) => {
        const studentId = String(item.StudentId !== undefined ? item.StudentId : (item.studentId || ''));
        const name = item.StudentName || item.studentName || '';
        const card = item.CardNumber || item.cardNumber || '';
        const key = studentId || (`${name}__${card}`);
        const rideTime = item.RideTime || item.rideTime || '';
        const processStatus = item.ProcessStatus !== undefined ? item.ProcessStatus : (item.processStatus || 0);
        const processStatusName = item.ProcessStatusName || item.processStatusName || '未知';
        const processedTime = item.ProcessedTime || item.processedTime || '';

        if (!map[key]) {
          map[key] = {
            studentId,
            studentName: name,
            StudentName: name,
            cardNumber: card,
            CardNumber: card,
            rideTimes: [],
            statusItems: [],
            processedTimes: []
          };
          order.push(key);
        }
        const row = map[key];
        if (rideTime && row.rideTimes.indexOf(rideTime) === -1) {
          row.rideTimes.push(rideTime);
        }
        row.statusItems.push({ status: processStatus, name: processStatusName, rideTime });
        if (processedTime) {
          row.processedTimes.push(processedTime);
        }
      });

      return order.map((key) => {
        const row = map[key];
        row.rideTimes.sort();
        const statusSummary = this.summarizeProcessStatus(row.statusItems);
        return {
          studentId: row.studentId,
          studentName: row.studentName,
          StudentName: row.studentName,
          cardNumber: row.cardNumber,
          CardNumber: row.cardNumber,
          rideTimes: row.rideTimes,
          slotCount: row.statusItems.length,
          processStatus: statusSummary.status,
          processStatusName: statusSummary.name,
          processedTime: row.processedTimes.length ? row.processedTimes.sort().slice(-1)[0] : ''
        };
      });
    },

    /**
     * 汇总同一学生多时段的推送状态。创建于 2026-09-16
     */
    summarizeProcessStatus(statusItems) {
      if (!statusItems || !statusItems.length) {
        return { status: 0, name: '待处理' };
      }
      const unique = {};
      statusItems.forEach((s) => {
        unique[s.status] = s.name;
      });
      const keys = Object.keys(unique);
      if (keys.length === 1) {
        const only = Number(keys[0]);
        return { status: only, name: unique[keys[0]] };
      }
      const doneCount = statusItems.filter((s) => s.status === 2).length;
      const total = statusItems.length;
      if (doneCount > 0 && doneCount < total) {
        return { status: 1, name: `部分完成 ${doneCount}/${total}` };
      }
      if (doneCount === total) {
        return { status: 2, name: '已处理完成' };
      }
      // 多种未完成状态并存
      return { status: 1, name: `混合状态 ${keys.length}种` };
    },

    onDetailSearch() {
      this.detailPage = 1;
    },

    onDetailPageChange(p) {
      this.detailPage = p;
    },

    detailIndexMethod(index) {
      return (this.detailPage - 1) * this.detailPageSize + index + 1;
    },

    onDetailDialogClosed() {
      this.detailKeyword = '';
      this.detailPage = 1;
      this.studentDetailAllList = [];
    },

    getStatusTagType(status) {
      switch (status) {
        case 0:
          return 'info'; // 待执行
        case 1:
          return 'warning'; // 部分完成
        case 2:
          return 'success'; // 全部完成
        case 3:
          return 'danger'; // 已取消
        default:
          return '';
      }
    },

    getStudentStatusTagType(status) {
      switch (status) {
        case 0:
          return 'info';
        case 1:
          return 'primary';
        case 2:
          return 'success';
        case 3:
          return 'warning';
        case 5:
          return 'danger';
        default:
          return '';
      }
    }
  }
};
</script>

<style scoped>
.record-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #eef2f6;
  margin-bottom: 10px;
  flex-shrink: 0;
}

.record-head__title-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.record-head__tag {
  background: #ecf5ff;
  color: #409eff;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid #d9ecff;
}

.record-head__title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.record-head__tip {
  font-size: 13px;
  color: #909399;
}

.filter-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 0 10px 0;
  flex-shrink: 0;
}

.filter-left {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-label {
  font-size: 13px;
  color: #606266;
  font-weight: 500;
}

.count-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: bold;
  font-size: 12px;
}

.count-plan {
  background: #f4f4f5;
  color: #606266;
}

.count-slot {
  background: #eef2ff;
  color: #4338ca;
}

.count-success {
  background: #f0f9eb;
  color: #67c23a;
}

.count-slot-done {
  background: #ecfdf5;
  color: #059669;
}

.ride-time-tags {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 4px;
  max-width: 100%;
}

.ride-time-tag {
  margin: 0 !important;
}

.ride-time-tag i {
  margin-right: 2px;
}

.ride-time-empty {
  color: #c0c4cc;
}

.detail-meta-banner {
  background: #f8fafc;
  border: 1px solid #eef2f6;
  border-radius: 8px;
  padding: 12px 14px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 16px;
  margin-bottom: 12px;
  font-size: 13px;
}

.detail-meta-banner .meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.detail-meta-banner .meta-item--times {
  grid-column: 1 / -1;
}

.detail-meta-banner .k {
  color: #94a3b8;
  flex-shrink: 0;
}

.detail-meta-banner .v {
  color: #0f172a;
  font-weight: 600;
}

.detail-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.detail-total-tip {
  font-size: 13px;
  color: #64748b;
}

.detail-total-tip b {
  color: #0f172a;
}

.detail-pager {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
}

.student-name {
  font-weight: 600;
  color: #303133;
}

.card-no {
  font-family: monospace;
  color: #606266;
}

.muted-text {
  font-size: 12px;
  color: #909399;
}

.slot-count-tip {
  margin-top: 2px;
  font-size: 11px;
  color: #94a3b8;
}

.empty-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #909399;
}

.empty-placeholder i {
  font-size: 64px;
  color: #dcdfe6;
  margin-bottom: 16px;
}

.empty-placeholder p {
  font-size: 14px;
  margin: 0;
}

@media screen and (max-width: 768px) {
  .record-head {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 12px;
  }
  .filter-bar {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
  .filter-left {
    width: 100%;
  }
}
</style>
